import escapeRegExp from 'lodash/escapeRegExp';

import { CLIENTLESS_ROUTES } from './constants';

/*
 * In our app previews the first segment will be preview--<preview-id>--<workspace>/ so we need to parse
 * this out to get the workspace name without dropping the preview id from the url which we need
 * for uniqueness (otherwise different previews would overwrite one another).
 *
 * Our app has too many places that assume a certain path structure so I gave up on trying to make
 * /preview/<preview-id>/<workspace>/ work.
 */
const PREVIEW_PATH_PREFIX = 'preview--';

const isPreviewWorkspaceSegment = (segment: string) =>
  segment.startsWith(PREVIEW_PATH_PREFIX);

export const isLocalhostShell =
  window.location.hostname.startsWith('clients.') &&
  window.location.pathname.split('/')[1]?.startsWith('localhost--');

export const isPreview =
  window.location.hostname.startsWith('clients.') &&
  isPreviewWorkspaceSegment(window.location.pathname.split('/')[1] ?? '');

export const parseWorkspace = (ws: string) => {
  if (isPreviewWorkspaceSegment(ws)) {
    return ws.split('--')[2] ?? ws;
  }
  if (ws.startsWith('localhost--')) {
    return ws.slice('localhost--'.length);
  }
  return ws;
};

/** True when `pathname` is already scoped to `workspace` (plain, preview, or localhost shell). */
export const isAtWorkspaceScopedPath = (
  pathname: string,
  workspace: string,
) => {
  const ws = escapeRegExp(workspace);
  return new RegExp(`^\\/(${ws}|preview--[^/]*--${ws}|localhost--${ws})`).test(
    pathname,
  );
};

const shouldSkipSubdomainCheck = (pathname: string): boolean =>
  pathname.includes('/redirects/evisync/') ||
  pathname.includes('folders-permission-check');

export const getRawClientFromUrl = () => {
  const url = window.location.pathname;
  const rawSubdomain = url.split('/')[1];
  const isClientlessRoute = (CLIENTLESS_ROUTES as string[]).includes(
    `/${rawSubdomain}`,
  );

  return isClientlessRoute ? '' : rawSubdomain;
};

export const getClientFromUrl = () => {
  const rawSubdomain = getRawClientFromUrl();

  if (rawSubdomain) {
    return parseWorkspace(rawSubdomain);
  }

  return '';
};

/**
 * Client slug to strip from Sentry event `transaction` names. For normal tenant
 * pages this is the BrowserRouter basename (`getRawClientFromUrl`), e.g. `nonga`.
 *
 * It returns '' for routes the app renders WITHOUT a real tenant prefix that
 * `getRawClientFromUrl` would otherwise hand back as if they were a tenant slug:
 * `folders-permission-check`, `/redirects/evisync/...`, and previews (all caught
 * by `shouldSkipSubdomainCheck`). Without this guard those pages would be
 * rewritten to `/:client` and tagged with a bogus `client_workspace`, polluting
 * the `/:client` bucket. Login-family clientless routes (`/login`, `/activation`
 * etc.) are already returned as '' by `getRawClientFromUrl` via CLIENTLESS_ROUTES.
 */
export const getSentryClientSlug = (): string =>
  shouldSkipSubdomainCheck(window.location.pathname)
    ? ''
    : getRawClientFromUrl();

/**
 * Replaces the leading workspace/client slug segment of a Sentry event's
 * `transaction` value with `:client`, so the same page aggregates across tenants
 * (e.g. `/nonga/documents` and `/acme/documents` both become
 * `/:client/documents`).
 *
 * Used for every event Sentry sends — both transactions (all pageloads,
 * navigations, and web-vital spans, not just LCP) and error events — so the
 * tenant slug never fragments Performance/Web Vitals or per-page error
 * aggregation. Pure string transform: the caller decides which events to apply
 * it to and supplies the slug.
 *
 * The slug is whatever the caller supplies — in the Sentry hooks this is
 * `getSentryClientSlug()`, which yields the BrowserRouter basename for tenant
 * pages and '' for routes with no real tenant prefix (login family,
 * `folders-permission-check`, `/redirects/evisync/...`, previews). Passing it in
 * keeps this function pure/testable; when the slug is '' the name is returned
 * unchanged, so those clientless routes keep their real transaction names
 * instead of collapsing into `/:client`.
 */
export const normalizeTransactionClient = (
  transactionName: string,
  clientSlug: string,
): string => {
  if (!clientSlug) {
    return transactionName;
  }

  const prefix = `/${clientSlug}`;
  if (transactionName === prefix) {
    return '/:client';
  }
  if (transactionName.startsWith(`${prefix}/`)) {
    return `/:client${transactionName.slice(prefix.length)}`;
  }
  return transactionName;
};

/**
 * Strips a single trailing slash from a Sentry transaction name, so
 * `/:client/documents/` and `/:client/documents` (React Router matches both
 * for routes with an optional trailing slash) merge into one aggregation
 * bucket instead of fragmenting Performance/Web Vitals and error grouping.
 * The root path `/` is left untouched since stripping it would yield ''.
 */
export const stripTrailingSlash = (transactionName: string): string =>
  transactionName.length > 1 && transactionName.endsWith('/')
    ? transactionName.slice(0, -1)
    : transactionName;

export const openInNewTab = (appPath: string) => {
  window.open(toClientScopedAppPath(appPath), '_blank', 'noopener');
};

/** Prefixes an app route with the current URL's client slug when present. */
export const toClientScopedAppPath = (appPath: string): string => {
  const subdomain = getRawClientFromUrl();
  return subdomain ? `/${subdomain}${appPath}` : appPath;
};

/** Receives a full URL and extracts the application route. */
export const getApplicationRoute = (fullUrl: string): string => {
  const url = new URL(fullUrl);
  const paths = url.pathname.split('/');
  const [_empty, _client, ...appRoutes] = paths;

  return `/${appRoutes.join('/')}`;
};

export type SubdomainCheckResult =
  | { action: 'none' }
  | { action: 'redirect'; subdomain: string }
  | { action: 'logout'; clientSubdomain: string };

/**
 * Determines what to do when the URL's client subdomain doesn't match
 * the authenticated user's subdomain.
 *
 * When the initial page load had no client in the URL (e.g., user typed
 * just the domain), the URL's first path segment may be a route path
 * misinterpreted as a client. In that case, redirect to the user's
 * subdomain instead of logging them out.
 */
export const getSubdomainCheckAction = ({
  pathname,
  userSubdomain,
  clientSubdomain,
  initialClient,
}: {
  pathname: string;
  userSubdomain: string;
  clientSubdomain: string;
  initialClient: string;
}): SubdomainCheckResult => {
  if (shouldSkipSubdomainCheck(pathname)) {
    return { action: 'none' };
  }

  const isPublicError = ['word-online-invalid-subscription'].includes(
    clientSubdomain,
  );

  if (
    !isPublicError &&
    userSubdomain &&
    clientSubdomain &&
    clientSubdomain !== userSubdomain
  ) {
    if (!initialClient) {
      return { action: 'redirect', subdomain: userSubdomain.toLowerCase() };
    }
    return { action: 'logout', clientSubdomain };
  }

  return { action: 'none' };
};

export const setSearchParams = (
  path: string,
  searchParams: Record<string, string> = {},
): string => {
  const url = new URL(path, window.location.origin);
  for (const [key, value] of Object.entries(searchParams)) {
    url.searchParams.set(key, value);
  }
  return url.toString();
};
